import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import Card from './ui/Card';
import Button from './ui/Button';

// Mock data for resources
const allResources = [
  {
    id: 1,
    title: 'Q1 2023 Win/Loss Analysis',
    description: 'Comprehensive analysis of our performance in the first quarter, highlighting key wins and areas for improvement.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
    date: 'April 15, 2023',
    author: 'Sarah Johnson'
  },
  {
    id: 2,
    title: 'Tech Industry Competitive Review',
    description: 'In-depth analysis of our position in the tech industry, comparing our offerings with key competitors.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
    date: 'May 22, 2023',
    author: 'Michael Chen'
  },
  {
    id: 3,
    title: 'Annual Market Share Report',
    description: 'Detailed breakdown of our market share across different segments, with projections for the coming year.',
    image: 'https://images.unsplash.com/photo-1553484771-11998c592b9c?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
    date: 'June 10, 2023',
    author: 'Emily Rodriguez'
  },
];

const Reports: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleResources = isExpanded ? allResources : allResources.slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-6 sm:mb-8 text-center">Reports</h1>
      
      <Card title="Resources" className="mb-6">
        <div className="flex justify-end">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            className="flex items-center text-sm"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-1 sm:mr-2" size={16} />
                <span className="hidden sm:inline">Collapse</span>
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 sm:mr-2" size={16} />
                <span className="hidden sm:inline">Expand</span>
              </>
            )}
          </Button>
        </div>
      </Card>
      
      <AnimatePresence>
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          layout
        >
          {visibleResources.map((resource) => (
            <motion.div
              key={resource.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <img src={resource.image} alt={resource.title} className="w-full h-40 object-cover" />
                <div className="p-4 flex flex-col flex-grow">
                  <h3 className="text-lg font-semibold text-blue-700 mb-2">{resource.title}</h3>
                  <p className="text-blue-600 text-sm mb-4 flex-grow">{resource.description}</p>
                  <div className="flex justify-between items-center text-xs sm:text-sm text-blue-500">
                    <span>{resource.date}</span>
                    <span>{resource.author}</span>
                  </div>
                  <a
                    href="#"
                    className="mt-4 flex items-center text-blue-600 hover:text-blue-800 transition duration-150 ease-in-out text-sm"
                  >
                    <FileText size={16} className="mr-2" />
                    View Resource
                  </a>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Reports;